// src/components/expenses/ExpenseFormModal.js
import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { formatLocalDate, parseLocalDate } from '../../utils/formatters'; // localstamp-e7c4a2b9 — LOCAL calendar day for the default expense date (was UTC via toISOString)

const ExpenseFormModal = ({ isOpen, onClose, onSave, expenseToEdit, jobs, currentUserProfile }) => {
    // UPDATED STATE: 'amount' is now Pre-Tax, 'taxAmount' is separate.
    const [formData, setFormData] = useState({
        vendor: '',
        category: '',
        amount: '',         // Pre-Tax Amount
        taxAmount: '',      // Tax Amount (New)
        date: formatLocalDate(),
        jobId: '',
        description: '',
        glAccountId: '',    // Expense GL
        taxGlAccountId: '', // Tax GL (New)
        bankAccountId: '',  // Payment Source
    });

    const [qbAccounts, setQbAccounts] = useState([]);
    const [qbBankAccounts, setQbBankAccounts] = useState([]);
    const [isQbLoading, setIsQbLoading] = useState(false);
    const [qbError, setQbError] = useState(null);

    // --- FIX: Define Permissions Once ---
    const hasFinancialAccess = ['Admin', 'Manager', 'Owner', 'Owner/System Admin', 'System Admin'].includes(currentUserProfile?.role);

    useEffect(() => {
        if (expenseToEdit) {
            setFormData({
                vendor: expenseToEdit.vendor || '',
                category: expenseToEdit.category || '',
                amount: expenseToEdit.amount || '', // Treats existing amount as Pre-Tax for simplicity
                taxAmount: expenseToEdit.taxAmount || '',
                date: expenseToEdit.date?.toDate 
                    ? expenseToEdit.date.toDate().toISOString().split('T')[0] 
                    : formatLocalDate(),
                jobId: expenseToEdit.jobId || '',
                description: expenseToEdit.description || '',
                glAccountId: expenseToEdit.glAccountId || '',
                taxGlAccountId: expenseToEdit.taxGlAccountId || '',
                bankAccountId: expenseToEdit.bankAccountId || '',
            });
        } else {
            setFormData({
                vendor: '', category: '', amount: '', taxAmount: '',
                date: formatLocalDate(),
                jobId: '', description: '',
                glAccountId: '', taxGlAccountId: '', bankAccountId: ''
            });
        }
    }, [expenseToEdit, isOpen]);

    useEffect(() => {
        // --- FIX: Use the new robust check ---
        if (isOpen && hasFinancialAccess) {
            loadQbData();
        }
    }, [isOpen, currentUserProfile, hasFinancialAccess]);

    const loadQbData = async () => {
        setIsQbLoading(true);
        setQbError(null);
        try {
            const getAccountsFn = httpsCallable(functions, 'getQuickBooksExpensesAccounts');
            const accResult = await getAccountsFn();
            setQbAccounts(accResult.data.accounts || []);

            const getBanksFn = httpsCallable(functions, 'getQuickBooksBankAccounts');
            const bankResult = await getBanksFn();
            setQbBankAccounts(bankResult.data.accounts || []);
        } catch (error) {
            console.error("QB Load Error:", error);
            setQbError("Failed to load QuickBooks accounts. Is QB connected?");
        } finally {
            setIsQbLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Calculate the total for your own records if needed, 
        // but we save split values to DB so the function can use them.
        const dataToSave = {
            ...formData,
            amount: parseFloat(formData.amount || 0),
            taxAmount: parseFloat(formData.taxAmount || 0),
            date: parseLocalDate(formData.date), // expdate-8b2e4f61 — interpret the picked YYYY-MM-DD as a LOCAL calendar day; new Date("YYYY-MM-DD") is UTC midnight → prev evening west of UTC, storing a day early
        };

        try {
            await onSave(dataToSave, expenseToEdit?.id);
            onClose();
        } catch (error) {
            alert("Error saving expense: " + error.message);
        }
    };

    if (!isOpen) return null;
    
    // Helper to show the total visually
    const totalDisplay = (parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)).toFixed(2);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-bold text-gray-800">
                        {expenseToEdit ? 'Edit Expense' : 'New Expense'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                            <input 
                                type="date" 
                                name="date" 
                                value={formData.date} 
                                onChange={handleChange}
                                className="w-full border rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" 
                                required 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pre-Tax Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500">$</span>
                                <input 
                                    type="number" 
                                    step="0.01" 
                                    name="amount" 
                                    value={formData.amount} 
                                    onChange={handleChange}
                                    className="w-full border rounded-md p-2 pl-7 focus:ring-blue-500 focus:border-blue-500" 
                                    required 
                                />
                            </div>
                        </div>
                    </div>

                    {/* NEW TAX FIELD ROW */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500">$</span>
                                <input 
                                    type="number" 
                                    step="0.01" 
                                    name="taxAmount" 
                                    value={formData.taxAmount} 
                                    onChange={handleChange}
                                    placeholder="0.00"
                                    className="w-full border rounded-md p-2 pl-7 focus:ring-blue-500 focus:border-blue-500" 
                                />
                            </div>
                        </div>
                        <div className="flex items-center pt-6">
                            <p className="font-bold text-lg text-gray-700">Total: ${totalDisplay}</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vendor / Payee</label>
                        <input 
                            type="text" 
                            name="vendor" 
                            value={formData.vendor} 
                            onChange={handleChange}
                            placeholder="e.g. Home Depot, Shell"
                            className="w-full border rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" 
                            required 
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Internal Category</label>
                        <input 
                            type="text" 
                            name="category" 
                            value={formData.category} 
                            onChange={handleChange}
                            placeholder="e.g. Materials, Fuel (Internal Use Only)"
                            className="w-full border rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" 
                            required 
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Job (Optional)</label>
                        <select 
                            name="jobId" 
                            value={formData.jobId} 
                            onChange={handleChange}
                            className="w-full border rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">-- General / Overhead --</option>
                            {/* UPDATED: ENHANCED OPTIONS */}
                            {jobs.map(job => (
                                <option key={job.id} value={job.id}>
                                    {job.jobNumber ? `#${job.jobNumber} ` : ''} 
                                    {job.title} 
                                    {job.clientName ? ` - ${job.clientName}` : ''}
                                    {job.customerDetails?.name ? ` - ${job.customerDetails.name}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description / Notes</label>
                        <textarea 
                            name="description" 
                            value={formData.description} 
                            onChange={handleChange}
                            rows="2"
                            className="w-full border rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                        ></textarea>
                    </div>

                    {/* --- FIX: Use the new robust check --- */}
                    {hasFinancialAccess && (
                        <div className="mt-6 p-4 bg-gray-50 border rounded-md space-y-3">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                    <span className="bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded font-bold">QB</span>
                                    QuickBooks Mapping
                                </h3>
                                {isQbLoading && <Loader2 size={16} className="animate-spin text-blue-600"/>}
                            </div>

                            {qbError && <p className="text-xs text-red-500">{qbError}</p>}

                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Paid From (Bank/Card)</label>
                                <select 
                                    name="bankAccountId" 
                                    value={formData.bankAccountId} 
                                    onChange={handleChange}
                                    disabled={isQbLoading}
                                    className="w-full text-sm border border-gray-300 rounded p-1.5 focus:border-blue-500"
                                >
                                    <option value="">Select Payment Source...</option>
                                    {qbBankAccounts.map(acc => (
                                        <option key={acc.id} value={acc.id}>{acc.name} ({acc.type})</option>
                                    ))}
                                </select>
                            </div>

                            {/* UPDATED MAPPING: Expense vs Tax Account */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Expense GL</label>
                                    <select 
                                        name="glAccountId" 
                                        value={formData.glAccountId} 
                                        onChange={handleChange}
                                        disabled={isQbLoading}
                                        className="w-full text-sm border border-gray-300 rounded p-1.5 focus:border-blue-500"
                                    >
                                        <option value="">Select Expense Account...</option>
                                        {/* Filter for Expense types */}
                                        {qbAccounts.filter(a => a.type.includes('Expense') || a.type.includes('Cost')).map(acc => (
                                            <option key={acc.id} value={acc.id}>{acc.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                        Tax GL (Input Tax Credits)
                                    </label>
                                    <select 
                                        name="taxGlAccountId" 
                                        value={formData.taxGlAccountId} 
                                        onChange={handleChange}
                                        disabled={isQbLoading}
                                        className="w-full text-sm border border-gray-300 rounded p-1.5 focus:border-blue-500"
                                    >
                                        <option value="">No separate tax tracking</option>
                                        {/* Filter for ASSET accounts only - NOT liability accounts like "GST/HST Payable" */}
                                        {/* For Canadian ITC tracking, use accounts like "GST/HST Paid" or "Input Tax Credits" */}
                                        {qbAccounts
                                            .filter(a => 
                                                a.type.includes('Asset') || 
                                                a.type.includes('Other Current Asset') ||
                                                a.name.toLowerCase().includes('gst paid') ||
                                                a.name.toLowerCase().includes('hst paid') ||
                                                a.name.toLowerCase().includes('input tax') ||
                                                a.name.toLowerCase().includes('tax paid')
                                            )
                                            .map(acc => (
                                                <option key={acc.id} value={acc.id}>
                                                    {acc.name} ({acc.type})
                                                </option>
                                            ))
                                        }
                                    </select>
                                    <p className="text-[10px] text-gray-500 mt-1">
                                        💡 Use "GST/HST Paid" (Asset), NOT "GST/HST Payable" (Liability)
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="pt-4 flex justify-end gap-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium transition-colors">
                            Cancel
                        </button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow-sm transition-colors">
                            Save Expense
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExpenseFormModal;